import { Box } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import { useEffect, useRef, useState, type ReactNode } from "react";

interface FadeInProps {
  children: ReactNode;
  /** Delay before animation starts (ms) */
  delay?: number;
  /** Animation duration (ms) */
  duration?: number;
  /** Y offset in pixels (positive = from below, negative = from above) */
  offsetY?: number;
  /** Trigger animation only once */
  once?: boolean;
  /** Intersection observer threshold (0-1) */
  threshold?: number;
  /** Additional sx props */
  sx?: SxProps;
}

export const FadeIn = ({
  children,
  delay = 0,
  duration = 300,
  offsetY = 20,
  once = true,
  threshold = 0.1,
  sx,
}: FadeInProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);

          if (once) {
            observer.unobserve(element);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [once, threshold]);

  return (
    <Box
      ref={ref}
      sx={{
        willChange: "transform, opacity",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "none" : `translateY(${offsetY}px)`,
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
